import { createContext, useContext, useCallback, useEffect, useState } from 'react'
import { supabase, isConfigured } from '../lib/supabase.js'
import { GLOSSARY as STATIC_GLOSSARY, glossarySlug } from '../data/glossary.js'
import { GLOSSARY_LONG } from '../data/glossaryLong.js'
import { usePeptides } from './PeptidesContext.jsx'

/* Glossary CMS context. Mirrors PeptidesContext: reads editable terms from the
   `glossary` table when the backend is connected, otherwise falls back to the
   bundled static terms (src/data/glossary.js) so /glossary never goes blank and
   stays prerendered for SEO. Admin status is reused from PeptidesContext, so
   this provider must nest inside <PeptidesProvider>. */

const GlossaryContext = createContext(null)

const rowToTerm = (r) => {
  const slug = r.slug || glossarySlug(r.term)
  return {
    id: r.id,
    term: r.term,
    slug,
    aka: r.aka || undefined,
    def: r.definition,
    // Backfill the expansion from the bundled data so it survives a DB import
    // (the CMS edits only the short definition).
    long: GLOSSARY_LONG[slug],
    sortOrder: r.sort_order ?? 100,
  }
}

const termToRow = (t, sortOrder) => ({
  term: t.term,
  slug: t.slug || glossarySlug(t.term),
  aka: t.aka?.trim() || null,
  definition: t.def,
  ...(sortOrder != null ? { sort_order: sortOrder } : {}),
})

const staticList = STATIC_GLOSSARY.map((t, i) => ({ ...t, sortOrder: i * 10 }))
const byTerm = (a, b) => a.term.localeCompare(b.term)

export function GlossaryProvider({ children }) {
  const { isAdmin } = usePeptides()
  const [glossary, setGlossary] = useState(staticList)
  const [source, setSource] = useState('static') // 'db' | 'empty' | 'static'

  const load = useCallback(async () => {
    if (!isConfigured) {
      setGlossary(staticList)
      setSource('static')
      return
    }
    const { data, error } = await supabase.from('glossary').select('*').order('term', { ascending: true })
    if (error || !data || data.length === 0) {
      setGlossary(staticList)
      setSource(error ? 'static' : 'empty')
    } else {
      setGlossary(data.map(rowToTerm).sort(byTerm))
      setSource('db')
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const createTerm = useCallback(async (t) => {
    const { data, error } = await supabase.from('glossary').insert(termToRow(t)).select().single()
    if (error) return { error: error.message }
    setGlossary((list) => [...list, rowToTerm(data)].sort(byTerm))
    setSource('db')
    return { record: rowToTerm(data) }
  }, [])

  const updateTerm = useCallback(async (id, t) => {
    const { data, error } = await supabase.from('glossary').update(termToRow(t)).eq('id', id).select().single()
    if (error) return { error: error.message }
    const rec = rowToTerm(data)
    setGlossary((list) => list.map((x) => (x.id === id ? rec : x)).sort(byTerm))
    return { record: rec }
  }, [])

  const deleteTerm = useCallback(async (id) => {
    const { error } = await supabase.from('glossary').delete().eq('id', id)
    if (error) return { error: error.message }
    setGlossary((list) => list.filter((x) => x.id !== id))
    return {}
  }, [])

  // Seed the DB from the bundled terms it's missing (safe to re-run).
  const seedStarter = useCallback(async () => {
    const have = source === 'db' ? new Set(glossary.map((t) => t.slug)) : new Set()
    const missing = staticList.filter((t) => !have.has(t.slug))
    if (missing.length === 0) return { count: 0 }
    const base = source === 'db' ? glossary.length : 0
    const rows = missing.map((t, i) => termToRow(t, (base + i) * 10))
    const { error } = await supabase.from('glossary').insert(rows)
    if (error) return { error: error.message }
    await load()
    return { count: rows.length }
  }, [glossary, source, load])

  const getTerm = useCallback((slug) => glossary.find((t) => t.slug === slug), [glossary])

  const value = { glossary, source, isAdmin, createTerm, updateTerm, deleteTerm, seedStarter, getTerm, reload: load }
  return <GlossaryContext.Provider value={value}>{children}</GlossaryContext.Provider>
}

export const useGlossary = () => useContext(GlossaryContext)
